import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { customerApi, orgApi } from "@/lib/entities";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { SectionHeader } from "@/components/SectionHeader";
import { DemoChip } from "@/components/Chips";
import { RoleGate } from "@/components/RoleGate";
import { CustomerFormDialog } from "@/pages/customers/CustomerFormDialog";
import { MobileListCard } from "@/components/MobileListCard";
import { ActionMenu, ArchiveConfirmModal, useArchive } from "@/components/ActionMenu";

// PRG · Wave 4B-6 canonical resolver. `can("customers.create")` auto-
// grants Standard Admin (role_class.is_admin=True OR role=="admin") the
// same write capability that Super Admin, leasing_sales, and
// ops_dispatch have on the operational Client roster.
const READ_ROLES = ["super_admin", "admin", "leasing_sales", "ops_dispatch", "accounting", "maintenance"];

export default function CustomersList() {
  const { user, can } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [activeOnly, setActiveOnly] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const canWrite = can("customers.create") || can("customers.edit");

  const load = async () => {
    try {
      const data = await customerApi.list({
        q: q || undefined,
        active: activeOnly ? true : undefined,
        include_archived: showArchived,
        page,
        page_size: 25,
      });
      setRows(data.results);
      setTotal(data.total);
    } catch (e) {
      toast.error("Failed to load customers");
    }
  };

  const archive = useArchive({
    entityLabel: "Customer",
    apiArchive: customerApi.archive,
    onSuccess: async (row) => {
      toast.success(`Archived ${row.display_name}`);
      await load();
    },
  });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, page, activeOnly, showArchived]);

  useEffect(() => {
    // load orgs once for the create form
    orgApi.list({ page_size: 200 }).then((d) => setOrgs(d.results)).catch(() => {});
  }, []);

  return (
    <RoleGate allow={READ_ROLES}>
      <PageShell>
        <PageHeader
          testid="page-customers"
          title="Clients"
          subtitle={`${total.toLocaleString()} total`}
          right={
            canWrite && (
              <button
                data-testid="new-customer-btn"
                onClick={() => setOpenCreate(true)}
                className="inline-flex items-center gap-1.5 bg-coach-blue hover:bg-deep-road-navy text-white text-[12.5px] font-medium px-3 py-1.5 rounded-sm"
              >
                <Plus size={14} /> New Customer
              </button>
            )
          }
        />

        <div className="bg-white border border-coach-silver rounded-md">
          <div className="px-4 pt-4">
            <SectionHeader testid="customers-section-directory">Client Directory</SectionHeader>
          </div>
          <div className="flex items-center gap-3 px-4 pb-3 border-b border-coach-silver">
            <input
              data-testid="customers-search"
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder="Search by name or customer number…"
              className="flex-1 max-w-md border border-coach-silver rounded-sm px-3 py-1.5 text-[13px]"
            />
            <label className="text-[12px] text-steel flex items-center gap-1.5">
              <input
                type="checkbox"
                data-testid="customers-active-only"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />{" "}
              Active only
            </label>
            <label className="text-[12px] text-steel flex items-center gap-1.5">
              <input
                type="checkbox"
                data-testid="customers-show-archived"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />{" "}
              Include archived
            </label>
          </div>

          <table className="hidden md:table w-full text-[13px]" data-testid="customers-table">
            <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
              <tr>
                {/* RC4 · ITEM 8 — Customer # column hidden from UI.
                    Identifier is still persisted server-side and
                    accepted by search/filter APIs for compatibility. */}
                <th className="text-left px-4 py-2">Display Name</th>
                <th className="text-left px-4 py-2">Trip Code</th>
                <th className="text-left px-4 py-2 w-24">Active</th>
                <th className="text-right px-4 py-2 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {rows && rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`customer-row-${r.customer_number}`}
                  className="border-t border-coach-silver/60 hover:bg-road-white"
                >
                  <td className="px-4 py-2">
                    <Link to={`/customers/${r.id}`} className="text-coach-blue hover:underline">
                      {r.display_name}
                    </Link>{" "}
                    {r.is_demo && <DemoChip />}
                  </td>
                  <td className="px-4 py-2 text-steel">{r.trip_code || "—"}</td>
                  <td className="px-4 py-2">
                    <span
                      className={
                        r.active
                          ? "text-touring-lime font-medium"
                          : "text-critical-red font-medium"
                      }
                    >
                      {r.active ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right">
                    <ActionMenu
                      testid={`customer-actions-${r.customer_number}`}
                      onView={() => nav(`/customers/${r.id}`)}
                      onEdit={canWrite ? () => nav(`/customers/${r.id}?edit=1`) : undefined}
                      onArchive={canWrite && !r.is_archived ? () => archive.request(r) : undefined}
                    />
                  </td>
                </tr>
              ))}
              {rows === null && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-steel text-[13px]">
                    Loading customers…
                  </td>
                </tr>
              )}
              {rows && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-steel text-[13px]">
                    No customers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3 p-3" data-testid="customers-mobile-list">
            {rows === null && (
              <div className="text-center text-steel text-[13px] py-8">Loading customers…</div>
            )}
            {rows && rows.length === 0 && (
              <div className="text-center text-steel text-[13px] py-8">No customers found.</div>
            )}
            {(rows || []).map((r) => (
              <MobileListCard
                key={r.id}
                to={`/customers/${r.id}`}
                testid={`customer-card-${r.customer_number}`}
                // RC4 · ITEM 8 — monoTag (customer #) hidden. Card
                // keeps display_name as primary.
                primary={r.display_name}
                secondary={r.trip_code ? `Trip code: ${r.trip_code}` : (r.active ? "Active customer" : "Inactive")}
                isDemo={r.is_demo}
                right={
                  <div className="flex items-center gap-1">
                    <span className={`text-[11px] font-semibold ${r.active ? "text-touring-lime" : "text-critical-red"}`}>
                      {r.active ? "ACTIVE" : "INACTIVE"}
                    </span>
                    <ActionMenu
                      testid={`customer-actions-mobile-${r.customer_number}`}
                      onView={() => nav(`/customers/${r.id}`)}
                      onEdit={canWrite ? () => nav(`/customers/${r.id}?edit=1`) : undefined}
                      onArchive={canWrite && !r.is_archived ? () => archive.request(r) : undefined}
                    />
                  </div>
                }
              />
            ))}
          </div>

          <Pager page={page} setPage={setPage} total={total} pageSize={25} />
        </div>

        {openCreate && (
          <CustomerFormDialog
            open={openCreate}
            orgs={orgs}
            onClose={() => setOpenCreate(false)}
            onSaved={() => {
              setOpenCreate(false);
              load();
            }}
          />
        )}
        <ArchiveConfirmModal {...archive.modalProps} />
      </PageShell>
    </RoleGate>
  );
}

export const Pager = ({ page, setPage, total, pageSize }) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-2 border-t border-coach-silver text-[12px] text-steel">
      <div>
        Page {page} of {pages}
      </div>
      <div className="flex gap-1">
        <button
          data-testid="pager-prev"
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
          className="px-2 py-1 border border-coach-silver rounded-sm disabled:opacity-40"
        >
          Prev
        </button>
        <button
          data-testid="pager-next"
          disabled={page >= pages}
          onClick={() => setPage(page + 1)}
          className="px-2 py-1 border border-coach-silver rounded-sm disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
};
